// @flow

import * as React from 'react';
import { connect } from 'react-redux';

import { CSSTransition, TransitionGroup } from 'react-transition-group';
import { actions } from '@ta/common.state';
import { Link } from '@ta/common.routing';
import classnames from '@ta/common.classnames';
import trackEventAJAX from '@ta/common.tracking';
import { SocialLoginGate } from '@ta/social.login-gate';
import { InteractionLogger } from '@ta/tracking.interactions';
import { BlockScroll } from '@ta/modal.core';

import Backdrop from './Backdrop';
import SocialActionButton from './SocialActionButton';
import type { UgcAction } from './UgcActionMapper';

import styles from './UploaderActionButton.less';

type State = {|
  show: boolean,
|};

/** Props exposed to consumers of the component */
type ExternalProps = {|
  actions: UgcAction[],
  /** Additional class styling to apply to the content's outermost HTML element */
  className?: string,
  /** Additional inline styling to apply to the content's outermost HTML element */
  style?: { [string]: number },
  /** A callback that fires whenever the primary button is clicked */
  onClick?: (SyntheticEvent<*>) => mixed,
  /** A callback that fires in the event where we transition to and from the uploading view */
  onChange?: (show: boolean) => mixed,
|};

type Props = {|
  ...ExternalProps,
  /** Internal; provided by redux store. Directly use redux action as opposed to <Opener/> because we need to open arbitrary overlays
   *  TODO: Long term vision is to move overlays out of the FAB altogether */
  showGlobalOverlay: string => mixed,
  page: string,
  isMember: boolean,
|};

// -------------------------------------------------------------------- //
// Make sure these values match the values in the less file             //
const STAGGER_DELAY = 30;
const TRANSITION_DURATION = 300;
/** The maximum number of actions to display */
export const ACTION_LIMIT = 10;
// -------------------------------------------------------------------- //

const actionTransitions = {
  enter: styles.enter,
  enterActive: styles.enter_active,
  exit: styles.exit,
  exitActive: styles.exit_active,
};

class UploaderActionButton extends React.Component<Props, State> {
  static defaultProps = {
    actions: [],
    onClick: undefined,
    onChange: undefined,
    className: undefined,
    style: undefined,
  };

  state = { show: false };

  onClick = (event: SyntheticEvent<*>) => {
    if (this.props.onClick) {
      this.props.onClick(event);
    }
    this.toggle();
  };

  toggle = (type: ?boolean) => {
    this.setState(
      prevState => ({ show: type != null ? type : !prevState.show }),
      () => {
        if (this.props.onChange) {
          this.props.onChange(this.state.show);
        }
      },
    );
  };

  render() {
    const { className, style, showGlobalOverlay, page, isMember } = this.props;
    const ugcActions = this.props.actions.slice(0, ACTION_LIMIT);
    const { show } = this.state;
    const totalDuration = TRANSITION_DURATION + ugcActions.length * STAGGER_DELAY + 50;
    // Don't render a Fab if there aren't any actions
    return (
      ugcActions.length > 0 && (
        <>
          {ugcActions.map(action =>
            action.overlay ? (
              /* Names of the overlays are unique */
              <React.Fragment key={action.name}>{action.overlay()}</React.Fragment>
            ) : null,
          )}
          {this.state.show && (
            <>
              <Backdrop
                onClick={() => {
                  this.toggle(false);
                }}
              />
              <BlockScroll iOS11BugFix={false} />
            </>
          )}
          <SocialLoginGate>
            {socialGate => (
              <div className={classnames(styles.upload_container, className)} style={style}>
                <InteractionLogger>
                  {logInteraction => (
                    <SocialActionButton
                      invert={show}
                      className={classnames(styles.primary_button, { [styles.show]: show })}
                      icon="plus"
                      onClick={e => {
                        logInteraction(e, 'postContentCtaClick', {
                          action: 'NO_SELECTION',
                          entrypoint: 'FAB',
                          isMemberSignedIn: isMember,
                        });
                        trackEventAJAX({
                          module: 'postContentCtaClick',
                          action: 'NO_SELECTION',
                          context: `FAB|${page}`,
                          isMemberSignedIn: isMember,
                        });
                        this.onClick(e);
                      }}
                    />
                  )}
                </InteractionLogger>
                <TransitionGroup component={null}>
                  {show &&
                    ugcActions.map(({ label, icon, onClick, ...options }) => (
                      <CSSTransition
                        classNames={actionTransitions}
                        timeout={totalDuration}
                        unmountOnExit
                        // Labels are reasonably unique
                        key={label}
                      >
                        {React.createElement(
                          options.url ? Link : 'span',
                          // $FlowFixMe92
                          { className: styles.upload_item, onClick, href: options.url },
                          <InteractionLogger>
                            {logInteraction => {
                              const wrappedOnClick = event => {
                                // Don't test interactions
                                /* istanbul ignore next */
                                if (options.trackingName !== undefined) {
                                  logInteraction(event, 'postContentCtaClick', {
                                    action: options.trackingName,
                                    entrypoint: 'FAB',
                                    isMemberSignedIn: isMember,
                                  });
                                  trackEventAJAX({
                                    module: 'postContentCtaClick',
                                    action: options.trackingName,
                                    context: `FAB|${page}`,
                                    isMemberSignedIn: isMember,
                                  });
                                }
                                if (options.url) {
                                  // Don't test events
                                  /* istanbul ignore next */
                                  // Stop immediate navigation, since we prefer users to log in first
                                  event.preventDefault();
                                }
                                socialGate(options.loginPid).then(() => {
                                  // Change the PID to the real PID of the product
                                  global.require(['trjs!ta/util/Cookie'], Cookie => {
                                    Cookie.setPIDCookie(options.pid);
                                  });
                                  if (options.name) {
                                    showGlobalOverlay(options.name);
                                  }
                                  if (onClick) {
                                    onClick(event);
                                  }
                                  // Don't test window objects
                                  /* istanbul ignore next */
                                  // eslint-disable-next-line no-restricted-globals
                                  if (options.url && window?.location) {
                                    // eslint-disable-next-line no-restricted-globals
                                    window.location.href = options.url;
                                  }
                                });
                                this.toggle(false);
                              };
                              return (
                                <>
                                  <SocialActionButton icon={icon} onClick={wrappedOnClick} />
                                  {label}
                                </>
                              );
                            }}
                          </InteractionLogger>,
                        )}
                      </CSSTransition>
                    ))}
                </TransitionGroup>
              </div>
            )}
          </SocialLoginGate>
        </>
      )
    );
  }
}

export default connect<Props, ExternalProps, _, _, _, _>(
  state => ({
    page: state.route.page,
    isMember: state.auth.isMember,
  }),
  {
    showGlobalOverlay: actions.overlays.showGlobalOverlay,
  },
)(UploaderActionButton);
